import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getApiBase } from "../apiBase";

const Icons = {
  Refresh: (p) => <svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>,
  Duplicate: (p) => <svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 01-2-2V5a2 2 0 012-2h4" /></svg>,
  Check: (p) => <svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>,
  Merge: (p) => <svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>,
  Ingest: (p) => <svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>,
  Edit: (p) => <svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>,
  Save: (p) => <svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>,
  ChevronRight: (p) => <svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>,
  Search: (p) => <svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
  Alert: (p) => <svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>,
  Close: (p) => <svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>,
  Group: (p) => <svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value.includes("T") ? value : value.replace(" ", "T") + "Z");
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function KBGaps() {
  const { tenantId, botId } = useParams();
  const apiBase = useMemo(() => getApiBase(), []);

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [error, setError] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [answerDraft, setAnswerDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergePrimary, setMergePrimary] = useState(null);
  const [mergeAnswer, setMergeAnswer] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  const [ingesting, setIngesting] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState(new Set());

  const loadPending = async () => {
    if (!tenantId || !botId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${apiBase}/tenant/${tenantId}/bot/${botId}/kb-gaps/pending`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setGroups(Array.isArray(data.groups) ? data.groups : []);
      setSelectedIds(new Set()); 
    } catch (e) {
      setError(e.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPending(); }, [tenantId, botId]);

  const handleCheckDuplicates = async () => {
    setCheckingDuplicates(true);
    try {
      const res = await fetch(`${apiBase}/tenant/${tenantId}/bot/${botId}/kb-gaps/check-duplicates`, { method: "POST" });
      if (!res.ok) throw new Error(await res.text());
      await loadPending();
    } catch (e) {
      setError(e.message);
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const handleSaveAnswer = async (ticket) => {
  if (!answerDraft.trim()) return;
  setSaving(true);

  try {
    const res = await fetch(
      `${apiBase}/tenant/${tenantId}/bot/${botId}/kb-gaps/${ticket.escalation_id}/answer`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ideal_answer: answerDraft }),
      }
    );

    if (!res.ok && res.status !== 409) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Save failed");
    }

    // ✅ update UI immediately
    setGroups(prev =>
      prev.map(group => ({
        ...group,
        tickets: group.tickets.map(t =>
          t.escalation_id === ticket.escalation_id
            ? { ...t, ideal_answer: answerDraft }
            : t
        )
      }))
    );

    setSelectedTicket(prev =>
      prev ? { ...prev, ideal_answer: answerDraft } : null
    );

  } catch (e) {
    setError(e.message);
  } finally {
    setSaving(false);
  }
};

const isEdited =
  selectedTicket &&
  answerDraft.trim() !== (selectedTicket.ideal_answer || "").trim();

  const handleMerge = async (group) => {
    if (!mergePrimary || !selectedTicket) return;
    try {
      const mergedIds = group.tickets.filter(t => t.escalation_id !== mergePrimary).map(t => t.escalation_id);
      const res = await fetch(`${apiBase}/tenant/${tenantId}/bot/${botId}/kb-gaps/merge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          primary_escalation_id: mergePrimary,
          merged_escalation_ids: mergedIds,
          combined_answer: mergeAnswer || selectedTicket.ideal_answer || "",
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      await loadPending();
      setMergeMode(false);
      setMergePrimary(null);
      setMergeAnswer("");
      setSelectedTicket(null);
    } catch (e) {
      setError(`Merge failed: ${e.message}`);
    }
  };

  const toggleSelection = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedIds(newSet);
  };

  const toggleSelectAllGroups = () => {
  if (selectedGroups.size === groups.length) {
    // deselect everything
    setSelectedGroups(new Set());
    setSelectedIds(new Set());
  } else {
    const allGroupIds = groups.map(g => g.group_id);
    setSelectedGroups(new Set(allGroupIds));

    // collect all ticket ids
    const allTicketIds = [];

    groups.forEach(group => {
      group.tickets?.forEach(ticket => {
        if (!ticket.synced_at && ticket.ideal_answer) {
          allTicketIds.push(ticket.escalation_id);
        }
      });
    });

    setSelectedIds(new Set(allTicketIds));
  }
};

  const toggleSelectAll = () => {
    if (!selectedGroup) return;
    const pendingIds = selectedGroup.tickets
      .filter(t => !t.synced_at && t.ideal_answer)
      .map(t => t.escalation_id);
    
    if (pendingIds.length === 0) return;

    const allSelected = pendingIds.every(id => selectedIds.has(id));
    const newSet = new Set(selectedIds);
    
    if (allSelected) {
      pendingIds.forEach(id => newSet.delete(id));
    } else {
      pendingIds.forEach(id => newSet.add(id));
    }
    setSelectedIds(newSet);
  };

  const initiateIngest = () => {
    if (selectedIds.size > 0) {
      setShowConfirm(true);
    } else if (selectedTicket && !selectedTicket.synced_at) {
      setShowConfirm(true);
    }
  };

  const confirmIngest = async () => {
    setShowConfirm(false);
    setIngesting(true);
    const idsToIngest = selectedIds.size > 0 
      ? Array.from(selectedIds) 
      : [selectedTicket.escalation_id];

    try {
      const res = await fetch(`${apiBase}/tenant/${tenantId}/bot/${botId}/kb-gaps/ingest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ escalation_ids: idsToIngest }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail?.message || data.error || "Ingest failed");
      
      setSuccessMsg(`Resolved & Ingested ${data.tickets_resolved} tickets successfully.`);
      setTimeout(() => setSuccessMsg(null), 4000);
      
      await loadPending();
      setSelectedTicket(null);
      setSelectedGroup(null);
      setSelectedIds(new Set());
    } catch (e) {
      setError(e.message);
    } finally {
      setIngesting(false);
    }
  };

  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groups;
    const q = searchQuery.toLowerCase();
    return groups.map(g => ({
      ...g,
      tickets: (g.tickets || []).filter(t => 
        t.original_query?.toLowerCase().includes(q) || 
        t.escalation_id?.toLowerCase().includes(q)
      )
    })).filter(g => g.tickets?.length > 0);
  }, [groups, searchQuery]);

  const totalPending = useMemo(() => 
    groups.reduce((sum, g) => sum + (g.tickets?.filter(t => !t.synced_at).length || 0), 0),
    [groups]
  );

  const handleGroupSelect = useCallback((group) => {
    setSelectedGroup(group);
    setSelectedTicket(null);
    setMergeMode(false);
    setMergePrimary(null);
    setSelectedIds(new Set());
  }, []);

  const handleTicketSelect = useCallback((ticket) => {
    setSelectedTicket(ticket);
    setAnswerDraft(ticket.ideal_answer || "");
    setMergeMode(false);
  }, []);

  const currentGroupSelectableCount = selectedGroup 
    ? selectedGroup.tickets.filter(t => !t.synced_at && t.ideal_answer).length 
    : 0;
  
  const currentGroupSelectedCount = selectedGroup
    ? selectedGroup.tickets.filter(t => !t.synced_at && t.ideal_answer && selectedIds.has(t.escalation_id)).length
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F4F2] via-white to-[#F8F4F2]">
      <div className="flex h-screen">
        <aside className="w-80 border-r border-[#5B3A60]/10 bg-white/80 backdrop-blur-sm flex flex-col">
          <div className="p-4 border-b border-[#5B3A60]/10">
           <div className="flex items-center justify-between mb-3">
  <h1 className="text-lg font-bold text-[#5B3A60]">Knowledge Gaps</h1>

  <button
    onClick={toggleSelectAllGroups}
    className="text-xs font-semibold text-[#5B3A60] hover:text-[#823B55]"
  >
    {selectedGroups.size === groups.length ? "Deselect All" : "Select All"}
  </button>
</div>
            <div className="relative">
              <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B3A60]/40" />
              <input
                type="text"
                placeholder="Search queries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-[#5B3A60]/5 border border-[#5B3A60]/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89D4F]/50 focus:border-[#B89D4F]/40 transition"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {loading ? (
              <div className="p-4 text-center text-sm text-[#5B3A60]/60">Loading...</div>
            ) : filteredGroups.length === 0 ? (
              <div className="p-4 text-center text-sm text-[#5B3A60]/60">No results</div>
            ) : (
              filteredGroups.map((group) => {
                const groupId = group.group_id ?? "unassigned";
                const pending = group.tickets?.filter(t => !t.synced_at).length || 0;
                const isSelected = selectedGroup?.group_id === group.group_id;
                
                return (
  <div
    key={groupId}
    className={`w-full p-3 rounded-xl transition-all duration-200 ${
      isSelected
        ? "bg-gradient-to-r from-[#5B3A60] to-[#6E4A6F] text-white shadow-lg shadow-[#5B3A60]/25"
        : "hover:bg-[#5B3A60]/5 text-[#5B3A60]"
    }`}
  >
    <div className="flex items-center gap-2">

      {/* GROUP CHECKBOX */}
      <input
  type="checkbox"
  checked={selectedGroups.has(group.group_id)}
  onChange={(e) => {
    e.stopPropagation();

    const newGroups = new Set(selectedGroups);
    const newIds = new Set(selectedIds);

    if (newGroups.has(group.group_id)) {
      newGroups.delete(group.group_id);

      group.tickets?.forEach(ticket => {
        newIds.delete(ticket.escalation_id);
      });

    } else {
      newGroups.add(group.group_id);

      group.tickets?.forEach(ticket => {
        if (!ticket.synced_at && ticket.ideal_answer) {
          newIds.add(ticket.escalation_id);
        }
      });
    }

    setSelectedGroups(newGroups);
    setSelectedIds(newIds);
  }}
  className="mt-1 w-4 h-4 accent-[#5B3A60] cursor-pointer"
/>
      {/* GROUP CLICK AREA */}
      <button
        onClick={() => handleGroupSelect(group)}
        className="flex-1 text-left"
      >
                    <div className="flex items-center gap-2 mb-1">
                      <Icons.Group className={`w-4 h-4 ${isSelected ? 'text-white/90' : 'text-[#5B3A60]/60'}`} />
                      <span className="font-medium text-sm truncate">
                        {group.group_id === null ? "Unassigned" : `Group #${group.group_id}`}
                      </span>
                      {group.is_duplicate_group && (
                        <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full ${
                          isSelected ? 'bg-white/25 text-white' : 'bg-[#B89D4F]/25 text-[#823B55]'
                        }`}>
                          Dup
                        </span>
                      )}
                    </div>
                    <div className={`text-xs ${isSelected ? 'text-white/75' : 'text-[#5B3A60]/50'}`}>
                      {pending} pending • {(group.tickets || []).length} total
                    </div>
                  </button>
                  </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="p-3 border-t border-[#5B3A60]/10">
            <button
              onClick={handleCheckDuplicates}
              disabled={checkingDuplicates}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-[#5B3A60] hover:bg-[#5B3A60]/5 rounded-xl transition disabled:opacity-50 border border-[#5B3A60]/20"
            >
              <Icons.Duplicate className="w-4 h-4" />
              {checkingDuplicates ? "Scanning..." : "Find Duplicates"}
            </button>
          </div>
        </aside>

        <main className="flex-1 flex flex-col overflow-hidden">
          <header className="px-6 py-4 border-b border-[#5B3A60]/10 bg-white/60 backdrop-blur-sm flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#5B3A60]">
                {selectedGroup 
                  ? (selectedGroup.group_id === null ? "Unassigned Tickets" : `Group #${selectedGroup.group_id}`)
                  : "Select a group"}
              </h2>
              <p className="text-sm text-[#5B3A60]/60">
                {selectedGroup 
                  ? `${selectedGroup.tickets?.filter(t => !t.synced_at).length || 0} pending tickets`
                  : "Choose a group from the sidebar to begin"}
              </p>
            </div>
            <button
              onClick={loadPending}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#5B3A60] hover:bg-[#5B3A60]/5 rounded-xl transition disabled:opacity-50 border border-[#5B3A60]/20"
            >
              <Icons.Refresh className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </header>

          <div className="flex-1 flex overflow-hidden">
            <div className={`w-96 border-r border-[#5B3A60]/10 bg-[#FAF7F5]/50 overflow-y-auto transition-all ${selectedTicket ? 'lg:w-80' : 'lg:w-96'}`}>
              {selectedGroup ? (
                <div className="p-3 space-y-2">
                  <div className="flex items-center justify-between px-2 mb-3">
                    <span className="text-xs font-bold text-[#5B3A60] uppercase tracking-wider">Tickets</span>
                    {/* {currentGroupSelectableCount > 0 && (
                      <button 
                        onClick={toggleSelectAll}
                        className="text-xs text-[#5B3A60] hover:text-[#823B55] font-semibold flex items-center gap-1 transition-colors"
                      >
                        {currentGroupSelectedCount === currentGroupSelectableCount ? 'Deselect All' : 'Select All Ready'}
                      </button>
                    )} */}
                  </div>
                  {(selectedGroup.tickets || []).map((ticket, index) => {
                    const isSelectedTicket = selectedTicket?.escalation_id === ticket.escalation_id;
                    const isSynced = !!ticket.synced_at;
                    const isChecked = selectedIds.has(ticket.escalation_id);
                    const canSelect = !isSynced && ticket.ideal_answer;
                    
                    return (
                      <div
                        key={ticket.escalation_id}
                        className={`group relative w-full p-4 rounded-xl border transition-all duration-200 ${
                          isSelectedTicket
                            ? 'border-[#B89D4F] bg-[#B89D4F]/10 shadow-md'
                            : isSynced
                              ? 'border-[#5B3A60]/10 bg-[#5B3A60]/5 opacity-60 cursor-not-allowed'
                              : 'border-[#5B3A60]/15 hover:border-[#B89D4F]/40 hover:bg-[#5B3A60]/5 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-start gap-3">

                          <button
                            onClick={() => !isSynced && handleTicketSelect(ticket)}
                            disabled={isSynced}
                            className="flex-1 text-left"
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <span className="font-mono text-xs font-semibold text-[#823B55]">
                                {ticket.escalation_id.slice(0, 8)}
                              </span>
                              {isSynced ? (
                                <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                                  <Icons.Check className="w-3.5 h-3.5" /> Synced
                                </span>
                              ) : (
                                <span className="text-xs text-[#5B3A60]/50">{formatDateTime(ticket.created_at)}</span>
                              )}
                            </div>
                            <p className="text-sm text-[#5B3A60] line-clamp-2 font-medium">{ticket.original_query}</p>
                            {/* {ticket.ideal_answer && !isSynced && (
                              <p className="mt-2 text-xs text-[#823B55] font-semibold flex items-center gap-1">
                                <Icons.Check className="w-3 h-3" /> Answer saved
                              </p>
                            )} */}
                            {!ticket.ideal_answer && !isSynced && (
                              <p className="mt-2 text-xs text-red-500 font-semibold flex items-center gap-1">
                                <Icons.Alert className="w-3 h-3" /> Missing answer
                              </p>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-[#5B3A60]/50 text-sm">
                  Select a group to view tickets
                </div>
              )}
            </div>

            <div className={`flex-1 overflow-y-auto transition-all ${selectedTicket ? 'p-6' : 'p-6 flex items-center justify-center'}`}>
              <AnimatePresence mode="wait">
                {selectedTicket ? (
                  <motion.div
                    key={selectedTicket.escalation_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="max-w-3xl mx-auto space-y-6"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm font-semibold text-[#823B55]">
                            {selectedTicket.escalation_id}
                          </span>
                          {selectedGroup?.is_duplicate_group && (
                            <span className="text-xs px-2 py-0.5 bg-[#B89D4F]/25 text-[#823B55] rounded-full font-semibold">
                              Duplicate Group
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg font-bold text-[#5B3A60]">User Query</h3>
                      </div>
                      <button
                        onClick={() => setSelectedTicket(null)}
                        className="p-2 text-[#5B3A60]/40 hover:text-[#5B3A60] hover:bg-[#5B3A60]/5 rounded-lg transition"
                      >
                        <Icons.Close className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="p-4 bg-gradient-to-br from-[#5B3A60]/5 to-[#B89D4F]/5 rounded-xl border border-[#5B3A60]/15">
                      <p className="text-[#5B3A60] font-medium">{selectedTicket.original_query}</p>
                      {selectedTicket.escalation_reason && (
                        <p className="mt-3 text-sm text-[#5B3A60]/60 italic border-l-2 border-[#B89D4F]/40 pl-3">
                          {selectedTicket.escalation_reason}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-[#5B3A60] mb-2">
                        Ideal Answer
                      </label>
                      <textarea
                        value={answerDraft}
                        onChange={(e) => setAnswerDraft(e.target.value)}
                        placeholder="Write the perfect answer for this query..."
                        className="w-full h-48 p-4 text-sm bg-white border border-[#5B3A60]/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89D4F]/50 focus:border-[#B89D4F]/40 resize-none transition"
                      />
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-[#5B3A60]/50">
                          {answerDraft.length} characters
                        </span>
                        <button
                          onClick={() => handleSaveAnswer(selectedTicket)}
                          disabled={saving || !answerDraft.trim()}
                          className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-[#5B3A60] to-[#6E4A6F] text-white text-sm font-semibold rounded-xl hover:from-[#6E4A6F] hover:to-[#5B3A60] transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#5B3A60]/25"
                        >
                          {saving ? (
                            <>
                              <Icons.Refresh className="w-4 h-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Icons.Save className="w-4 h-4" />
                              {selectedTicket?.ideal_answer
                                ? isEdited
                                  ? "Save Changes"
                                  : "Saved"
                                : "Save Answer"}
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {selectedGroup?.is_duplicate_group && (
                      <div className="pt-4 border-t border-[#5B3A60]/10">
                        {!mergeMode ? (
                          <button
                            onClick={() => { setMergeMode(true); setMergePrimary(selectedTicket.escalation_id); setMergeAnswer(answerDraft); }}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-[#823B55] hover:bg-[#823B55]/10 rounded-xl transition border border-[#823B55]/30"
                          >
                            <Icons.Merge className="w-4 h-4" />
                            Merge with other tickets in this group
                          </button>
                        ) : (
                          <div className="space-y-4 p-4 bg-gradient-to-br from-[#823B55]/10 to-[#B89D4F]/10 rounded-xl border border-[#823B55]/30">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-bold text-[#823B55]">Merge Tickets</span>
                              <button
                                onClick={() => { setMergeMode(false); setMergePrimary(null); }}
                                className="text-xs text-[#823B55]/70 hover:text-[#823B55] font-semibold"
                              >
                                Cancel
                              </button>
                            </div>
                            
                            <div>
                              <label className="block text-xs text-[#823B55]/80 mb-1 font-semibold">Primary Ticket</label>
                              <select
                                value={mergePrimary || ""}
                                onChange={(e) => setMergePrimary(e.target.value)}
                                className="w-full px-3 py-2 text-sm bg-white border border-[#823B55]/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B89D4F]/50"
                              >
                                {(selectedGroup.tickets || []).map(t => (
                                  <option key={t.escalation_id} value={t.escalation_id}>
                                    {t.escalation_id.slice(0, 8)} — {t.original_query?.slice(0, 40)}...
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-xs text-[#823B55]/80 mb-1 font-semibold">Combined Answer</label>
                              <textarea
                                value={mergeAnswer}
                                onChange={(e) => setMergeAnswer(e.target.value)}
                                className="w-full h-32 p-3 text-sm bg-white border border-[#823B55]/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B89D4F]/50 resize-none"
                              />
                            </div>
                            
                            <button
                              onClick={() => handleMerge(selectedGroup)}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#823B55] to-[#9B4A6F] text-white text-sm font-bold rounded-xl hover:from-[#9B4A6F] hover:to-[#823B55] transition shadow-lg shadow-[#823B55]/25"
                            >
                              <Icons.Merge className="w-4 h-4" />
                              Confirm Merge
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* {!selectedTicket.synced_at && (
                      <div className="pt-4 border-t border-[#5B3A60]/10">
                        <button
                          onClick={initiateIngest}
                          disabled={!selectedTicket.ideal_answer || ingesting}
                          className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-white text-sm font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#5B3A60]/30 ${
                            ingesting 
                              ? 'bg-gradient-to-r from-[#5B3A60] via-[#7C527A] to-[#5B3A60] bg-[length:200%_auto] animate-shimmer' 
                              : 'bg-gradient-to-r from-[#5B3A60] to-[#823B55] hover:from-[#823B55] hover:to-[#5B3A60]'
                          }`}
                        >
                          {ingesting ? (
                            <>
                              <Icons.Refresh className="w-4 h-4 animate-spin" />
                              Ingesting...
                            </>
                          ) : (
                            <>
                              <Icons.Ingest className="w-4 h-4" />
                              Ingest to Knowledge Base
                            </>
                          )}
                        </button>
                        {!selectedTicket.ideal_answer && (
                          <p className="mt-2 text-xs text-center text-[#5B3A60]/60 font-medium">
                            Add an answer before ingesting
                          </p>
                        )}
                      </div>
                    )} */}
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center"
                  >
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#5B3A60]/15 to-[#B89D4F]/15 flex items-center justify-center border border-[#5B3A60]/20">
                      <Icons.Group className="w-8 h-8 text-[#5B3A60]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#5B3A60] mb-1">
                      {selectedGroup ? "Select a ticket" : "No group selected"}
                    </h3>
                    <p className="text-sm text-[#5B3A60]/60 max-w-sm">
                      {selectedGroup 
                        ? "Click on a ticket from the list to view details and add answers"
                        : "Choose a group from the sidebar to start reviewing knowledge gaps"}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          
          {selectedIds.size > 0 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-md border border-[#5B3A60]/20 shadow-2xl rounded-2xl px-6 py-3 flex items-center gap-4 z-40 animate-slide-up">
              <span className="text-sm font-bold text-[#5B3A60]">
                {selectedIds.size} ticket{selectedIds.size > 1 ? 's' : ''} selected
              </span>
              <div className="h-4 w-px bg-[#5B3A60]/20"></div>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-sm text-[#5B3A60]/70 hover:text-[#5B3A60] font-semibold transition"
              >
                Cancel
              </button>
              <button
                onClick={initiateIngest}
                disabled={ingesting}
                className={`flex items-center gap-2 px-5 py-2 text-white text-sm font-bold rounded-xl transition shadow-md ${
                   ingesting 
                    ? 'bg-gradient-to-r from-[#5B3A60] via-[#7C527A] to-[#5B3A60] bg-[length:200%_auto] animate-shimmer' 
                    : 'bg-gradient-to-r from-[#5B3A60] to-[#823B55] hover:from-[#823B55] hover:to-[#5B3A60]'
                }`}
              >
                {ingesting ? (
                  <>
                    <Icons.Refresh className="w-4 h-4 animate-spin" />
                    Ingesting...
                  </>
                ) : (
                  <>
                    <Icons.Ingest className="w-4 h-4" />
                    Ingest All
                  </>
                )}
              </button>
            </div>
          )}
        </main>
      </div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 px-4 py-3 bg-red-500 text-white text-sm rounded-xl shadow-lg flex items-center gap-2 z-50"
          >
            <Icons.Alert className="w-4 h-4" />
            {error}
            <button onClick={() => setError(null)} className="ml-2 hover:opacity-80">
              <Icons.Close className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {successMsg && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            className="fixed bottom-6 right-6 px-6 py-4 bg-gradient-to-r from-green-600 to-green-500 text-white text-sm rounded-2xl shadow-2xl flex items-center gap-3 z-50 border border-green-400"
          >
            <div className="bg-white/25 p-1.5 rounded-full">
              <Icons.Check className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold">Success</p>
              <p className="opacity-95">{successMsg}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-[#5B3A60]/15"
            >
              <div className="flex items-center gap-3 mb-4 text-[#5B3A60]">
                <div className="p-2 bg-gradient-to-br from-[#5B3A60]/15 to-[#B89D4F]/15 rounded-full border border-[#5B3A60]/20">
                  <Icons.Ingest className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold">Confirm Ingestion</h3>
              </div>
              <p className="text-[#5B3A60]/80 mb-6 leading-relaxed">
                Are you sure you want to ingest {selectedIds.size > 0 ? selectedIds.size : 1} ticket{selectedIds.size > 1 || selectedIds.size === 0 ? 's' : ''} into the Knowledge Base? This will resolve them.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-4 py-2 text-sm font-semibold text-[#5B3A60]/70 hover:text-[#5B3A60] hover:bg-[#5B3A60]/5 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmIngest}
                  disabled={ingesting}
                  className="px-6 py-2 bg-gradient-to-r from-[#5B3A60] to-[#823B55] text-white text-sm font-bold rounded-xl hover:from-[#823B55] hover:to-[#5B3A60] transition shadow-lg shadow-[#5B3A60]/25 disabled:opacity-50"
                >
                  {ingesting ? 'Processing...' : 'Confirm'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .animate-shimmer {
          animation: shimmer 2s linear infinite;
        }
        @keyframes slide-up {
          from { transform: translate(-50%, 100%); opacity: 0; }
          to { transform: translate(-50%, 0); opacity: 1; }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}